import { useState, useEffect, useMemo } from 'react';
import useAPI from '../../api';
import { useFieldParameters } from './binding/useFieldParameters';

/**
 * The option list a field offers, declared inline as `options` or fetched from the
 * DataSourceRegistry key in `optionsUrl`. A declared list wins, and nothing is fetched for it.
 *
 * A key that answers differently per branch, per section or per sibling field is asked with the
 * parameters the field declares, gathered by `useFieldParameters`. A field waiting on a sibling it
 * depends on is not asked at all, and offers no list until that sibling is filled in.
 *
 * @param {Object} field - The field descriptor.
 * @param {String} basePath - The surface's branch of the form store.
 * @param {Object} owned - The field's own `value` and `onChange`, for clearing what it holds when a
 *   dependency stops being set.
 *
 * @returns {Array} `[options, loading, waiting]` - `waiting` holds the keys of the unset
 *   dependencies, empty where the field is free to ask.
 */
export const useLookupOptions = (field, basePath = null, owned = null) => {
  const API = useAPI();
  const { parameters, ready, waitingOn } = useFieldParameters(
    field,
    basePath,
    owned
  );
  const [options, setOptions] = useState(field.options || []);
  const [loading, setLoading] = useState(false);

  // Held as text so the fetch below runs when a parameter's value changes rather than whenever the
  // object holding it is rebuilt.
  const asked = useMemo(() => JSON.stringify(parameters), [parameters]);

  useEffect(() => {
    if (field.options) setOptions(field.options);
  }, [field.options]);

  useEffect(() => {
    if (!field.optionsUrl || field.options?.length > 0) return undefined;

    if (!ready) {
      setOptions([]);
      setLoading(false);

      return undefined;
    }

    let isMounted = true;
    setLoading(true);

    API.lookupDynamicOptionsBySearch(
      field.optionsUrl,
      JSON.parse(asked),
      (data) => {
        if (isMounted) {
          setOptions(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      },
      (err) => {
        console.error(`Failed to fetch options for ${field.key}`, err);
        if (isMounted) setLoading(false);
      }
    );

    return () => {
      isMounted = false;
    };
  }, [field.optionsUrl, field.options, field.key, asked, ready]);

  return [options, loading, waitingOn];
};

export default useLookupOptions;
